import { useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import type { AnyApiResponse, ApiResponse } from '@/types/api';
import { ensureSuccess, unwrapData, softUnwrap } from '@/utils/apiResponse';

interface HandleOptions {
  successToast?: boolean | string; // true uses resp.message; string overrides
  errorToast?: boolean | string;   // true uses resp.message; string overrides
}

export function useApiResponse() {
  const { toast } = useToast();

  const handle = useCallback(<T,>(resp: AnyApiResponse<T>, opts?: HandleOptions): ApiResponse<T> => {
    const ok = ensureSuccess<T>(resp);
    if (opts?.successToast) {
      toast({ title: typeof opts.successToast === 'string' ? opts.successToast : (ok.message || 'Success') });
    }
    return ok;
  }, [toast]);

  const data = useCallback(<T,>(resp: AnyApiResponse<T>, opts?: HandleOptions): T | null => {
    try {
      const value = unwrapData<T>(resp);
      if (opts?.successToast) {
        const msg = (resp as any)?.message || 'Success';
        toast({ title: typeof opts.successToast === 'string' ? opts.successToast : msg });
      }
      return value;
    } catch (e: any) {
      if (opts?.errorToast) {
        const msg = typeof opts.errorToast === 'string' ? opts.errorToast : (e?.message || 'Request failed');
        toast({ title: msg, variant: 'destructive' });
      }
      throw e;
    }
  }, [toast]);

  const soft = useCallback(<T,>(resp: AnyApiResponse<T>) => softUnwrap<T>(resp), []);

  return { handle, data, soft };
}
